import { PeopleTeamFilled } from '@fluentui/react-icons';
import HelpIcon from '@mui/icons-material/Help';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import { Box, InputAdornment, InputLabel, TextField, Tooltip } from '@mui/material';
import { Controller, FieldValues } from 'react-hook-form';

import CalendlyLogo from '../Icons/Logos/CalendlyLogo';
import SignalLogo from '../Icons/Logos/SignalLogo';
import { SlackLogo } from '../Icons/Logos/SlackLogo';
import TelegramLogo from '../Icons/Logos/TelegramLogo';
import NavigationLink from '../Link/NavigationLink';
import { BaseInputProps } from './GetFormInput';

const socialLinkHelpUrls = {
    calendly:
        'https://help.calendly.com/hc/en-us/articles/223193448-How-to-share-your-scheduling-link#sharing-your-scheduling-link-0-0',
    linkedin: 'https://www.linkedin.com/help/linkedin/answer/a522735/find-your-linkedin-public-profile-url?lang=en',
    signal: 'https://support.signal.org/hc/en-us/articles/360051086971-Group-Link-or-QR-code',
    slack: 'https://slack.com/help/articles/221769328-Locate-your-Slack-URL-or-ID',
    teams: 'https://support.microsoft.com/en-us/office/create-a-link-or-a-code-for-joining-a-team-in-microsoft-teams-11b0de3b-9288-4cb4-bc49-795e7028296f',
    telegram: 'https://telegram.org/faq?setln=uz#q-how-does-t-me-work',
    twitter: 'https://help.x.com/en/using-x/post-and-moment-url',
    whatsapp: 'https://faq.whatsapp.com/552523704867276/?locale=en_US',
};

export function FormSocialLink<T extends FieldValues>({
    control,
    disabled,
    fullWidth,
    helperText,
    iconType,
    label,
    required,
    small,
    validation,
    value,
}: Readonly<BaseInputProps<T>>) {
    let IconComponent;
    let HelpLink;
    switch (iconType) {
        case 'linkedin':
            IconComponent = <LinkedInIcon sx={{ color: '#0077B5', height: 30, width: 'auto' }} />;
            HelpLink = socialLinkHelpUrls.linkedin;
            break;
        case 'twitter':
            IconComponent = <XIcon sx={{ color: '#000000', height: 30, width: 'auto' }} />;
            HelpLink = socialLinkHelpUrls.twitter;
            break;
        case 'calendly':
            IconComponent = <CalendlyLogo />;
            HelpLink = socialLinkHelpUrls.calendly;
            break;
        case 'telegram':
            IconComponent = <TelegramLogo />;
            HelpLink = socialLinkHelpUrls.telegram;
            break;
        case 'teams':
            IconComponent = <PeopleTeamFilled style={{ color: '#6264A7', height: 30, width: 'auto' }} />;
            HelpLink = null;
            break;
        case 'teamsGroup':
            IconComponent = <PeopleTeamFilled style={{ color: '#6264A7', height: 30, width: 'auto' }} />;
            HelpLink = socialLinkHelpUrls.teams;
            break;
        case 'website':
            IconComponent = <LanguageIcon sx={{ color: 'mlBlue.main', height: 30, width: 'auto' }} />;
            HelpLink = null;
            break;
        case 'slack':
            IconComponent = <SlackLogo sx={{ color: '#6264A7', height: 50, ml: -1.35, mr: -1.35, width: 50 }} />;
            HelpLink = socialLinkHelpUrls.slack;
            break;
        case 'signal':
            IconComponent = <SignalLogo />;
            HelpLink = socialLinkHelpUrls.signal;
            break;
        default:
            IconComponent = null;
            HelpLink = null;
            break;
    }

    return (
        <Controller
            control={control}
            name={value}
            render={({ field: { onChange, value: inputValue }, fieldState: { error } }) => {
                const displayValue =
                    inputValue && (inputValue as string).replace('https://teams.microsoft.com/l/chat/0/0?users=', '');
                return (
                    <Box>
                        <InputLabel sx={{ color: 'black', fontWeight: 'medium', mb: 0.5 }}>{label}</InputLabel>
                        <TextField
                            disabled={disabled}
                            error={!!error}
                            fullWidth={fullWidth}
                            helperText={error?.message || helperText}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                if (iconType === 'teams') {
                                    onChange('https://teams.microsoft.com/l/chat/0/0?users=' + newValue);
                                } else {
                                    onChange(newValue);
                                }
                            }}
                            size={small ? 'small' : 'medium'}
                            slotProps={{
                                input: {
                                    endAdornment: HelpLink && (
                                        <InputAdornment position="end">
                                            <NavigationLink external openInNewTab to={HelpLink}>
                                                <Tooltip
                                                    title={`Click here to learn more about linking your ${label ?? 'social account'}`}
                                                >
                                                    <HelpIcon sx={{ color: 'mlBlue.main' }} />
                                                </Tooltip>
                                            </NavigationLink>
                                        </InputAdornment>
                                    ),
                                    startAdornment: <InputAdornment position="start">{IconComponent}</InputAdornment>,
                                },
                            }}
                            value={displayValue}
                            variant="outlined"
                        />
                    </Box>
                );
            }}
            rules={{
                required: required ? `${label ?? value} is required` : false,
                validate: (value) => {
                    if (value) {
                        const result = validation?.safeParse(value);
                        return result?.success ? true : result?.error.errors[0]?.message;
                    }
                },
            }}
        />
    );
}
